import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Leaf, Pill, Sprout, Microscope, Clock, ExternalLink, Plus, Check } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LanguageContext";
import api, { apiError } from "@/lib/api";

const AddToStack = ({ c, i, t }) => {
  const { user } = useAuth();
  const { lang } = useLang();
  const navigate = useNavigate();
  const [added, setAdded] = useState(false);
  const add = async () => {
    if (!user) { navigate("/register"); return; }
    try {
      const { data } = await api.post("/stack", { product_name: c.name, ingredients: [{ name: c.name, amount: c.studied_doses }], language: lang });
      setAdded(true);
      const it = data.interaction;
      if (it?.level === "avoid") toast.error(`${t("ix.avoid")}: ${it.note}`, { duration: 9000 });
      else if (it?.level === "caution") toast.warning(`${t("ix.caution")}: ${it.note}`, { duration: 9000 });
      else toast.success(user.medications ? `${t("stack.addedFromCond")} · ${t("ix.none")}` : `${t("stack.addedFromCond")}. ${t("ix.addMeds")}`);
    } catch (e) { toast.error(apiError(e)); }
  };
  return (
    <button onClick={add} disabled={added} data-testid={`condition-compound-add-stack-${i}`}
      className="ml-auto inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1 text-xs font-bold text-emerald-700 transition-colors hover:bg-emerald-100 disabled:opacity-70">
      {added ? <Check size={13} /> : <Plus size={13} />} {added ? t("stack.inStack") : t("stack.addBtn")}
    </button>
  );
};

const ICONS = { herb: Leaf, natural_product: Sprout, probiotic: Microscope, nutrient: Pill };
const STRENGTH = {
  strong: "bg-emerald-100 text-emerald-700",
  moderate: "bg-blue-100 text-blue-700",
  limited: "bg-amber-100 text-amber-700",
  mixed: "bg-gray-100 text-gray-700",
};

export const CompoundCard = ({ c, i, t }) => {
  const Icon = ICONS[c.category] || Pill;
  return (
    <div data-testid={`condition-compound-card-${i}`} className="rounded-xl border border-gray-200 bg-white p-4">
      <div className="mb-2 flex flex-wrap items-center gap-2">
        <Icon size={16} className="text-emerald-600" />
        <h4 className="font-bold text-gray-900">{c.name}</h4>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-600">{t(`cond.cat.${c.category}`)}</span>
        {c.evidence_strength && (
          <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${STRENGTH[c.evidence_strength] || STRENGTH.mixed}`}>
            {t(`cond.str.${c.evidence_strength}`)}
          </span>
        )}
        {c.evidence_stage && <span className="text-xs text-gray-400">{c.evidence_stage}</span>}
        <AddToStack c={c} i={i} t={t} />
      </div>
      <div className="mb-2 grid gap-2 sm:grid-cols-2">
        <p className="rounded-lg bg-emerald-50 p-2 text-sm text-emerald-900" data-testid={`condition-compound-dose-${i}`}>
          <span className="font-bold">{t("cond.dose")}: </span>{c.studied_doses}
        </p>
        <p className="flex items-start gap-1 rounded-lg bg-gray-50 p-2 text-sm text-gray-700">
          <Clock size={14} className="mt-0.5 shrink-0" /><span><span className="font-bold">{t("cond.duration")}: </span>{c.duration}</span>
        </p>
      </div>
      <p className="text-sm leading-relaxed text-gray-700">{c.findings}</p>
      {c.pmids?.length > 0 && (
        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs">
          <span className="font-semibold text-gray-400">{t("cond.sources")}:</span>
          {c.pmids.map((p) => (
            <a key={p} href={`https://pubmed.ncbi.nlm.nih.gov/${p}/`} target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-0.5 font-semibold text-emerald-600 hover:underline">PMID {p} <ExternalLink size={11} /></a>
          ))}
        </div>
      )}
    </div>
  );
};
